import {
  getFilteredCorrespondenceInbox,
  useCreateCorrespondenceThreadMutation,
  useMarkCorrespondencesAsReadMutation,
  useArchiveCorrespondencesMutation,
} from "toptaal-shared"
import { fetchFilteredStudents } from "@/api/services/student"
import ScreenWrapper from "@/components/ScreenWrapper"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import {
  CorrespondenceEntityType,
  CorrespondenceOverview,
  CorrespondenceThread,
  MAX_PAGINATED_PAGE_SIZE,
  useCorrespondenceRoute,
  createTableSearchSchema,
} from "toptaal-shared"
import { getFilteredProjects } from "@/api/services/project"
import { correspondenceQueryKeys } from "toptaal-shared"
import { organizationUserCan } from "@/utils/organizationUserCan"
import { checkOrganizationPermissions } from "@/utils/checkOrganizationPermissions"
import { getFilteredGroups } from "@/api/services/groups"
import { useOrganizationUserQuery } from "@/api/queries/organizationUser/useOrganizationUserQuery"
import { z } from "zod"

export const Route = createFileRoute("/correspondentie/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
  beforeLoad: () => checkOrganizationPermissions("read", "correspondence"),
})

function RouteComponent() {
  const router = useRouter()

  const searchParams = useSearch({ from: "/correspondentie/" })
  const navigate = useNavigate({ from: "/correspondentie/" })
  const { data: user } = useOrganizationUserQuery()

  const { archived } = searchParams

  const queryKey = archived
    ? correspondenceQueryKeys.archivedInbox()
    : correspondenceQueryKeys.inbox()

  const correspondenceProps = useCorrespondenceRoute({
    entityId: null,
    entityType: null,
    queryKey,
    queryFn: (filters) =>
      getFilteredCorrespondenceInbox({ filters: { ...filters, archived } }),
    useCreateMutation: useCreateCorrespondenceThreadMutation,
    searchParams,
    navigate,
  })

  const markAsReadMutation = useMarkCorrespondencesAsReadMutation({
    queryKey,
  })

  const archiveMutation = useArchiveCorrespondencesMutation({
    queryKey,
    action: archived ? "unarchive" : "archive",
  })

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
      }),
    })
  }

  const handleNavigateToThread = (thread: CorrespondenceThread) => {
    if (thread.unreadCount > 0) {
      markAsReadMutation.mutate([thread.id])
    }

    router.navigate({
      to: "/correspondentie/$threadId",
      params: { threadId: thread.id.toString() },
      search: {
        entityType: thread.entityType,
        entityId: thread.entityId,
      },
    })
  }

  const loadEntityOptions = async (entityType: CorrespondenceEntityType) => {
    switch (entityType) {
      case "Student": {
        const students = await fetchFilteredStudents({
          page: 1,
          pageSize: MAX_PAGINATED_PAGE_SIZE,
          search: "",
        })

        return students.data.students.map((student) => ({
          value: student.id.toString(),
          label: `${student.firstName} ${student.lastName}`,
        }))
      }
      case "Project": {
        const projects = await getFilteredProjects({
          page: 1,
          pageSize: MAX_PAGINATED_PAGE_SIZE,
          search: "",
        })

        return projects.data.projects.map((project) => ({
          value: project.id.toString(),
          label: project.name,
        }))
      }
      case "LessonGroup": {
        const groups = await getFilteredGroups({
          page: 1,
          pageSize: MAX_PAGINATED_PAGE_SIZE,
          search: "",
        })

        return groups.data.lessonGroups.map((group) => ({
          value: group.id.toString(),
          label: `Groep ${group.id}`,
        }))
      }
      default:
        return []
    }
  }

  return (
    <ScreenWrapper className="!px-0 py-8 screen-height-minus-navbar">
      <CorrespondenceOverview
        {...correspondenceProps}
        handleNavigateToThread={handleNavigateToThread}
        contextType="inbox"
        loadEntityOptions={loadEntityOptions}
        canCreateCorrespondence={organizationUserCan(
          "create",
          "correspondence",
        )}
        correspondencePermission={{
          root: "org",
          roles: user.roles,
          baseUserId: user.baseUserId,
        }}
        markAsReadMutation={markAsReadMutation}
        archiveMutation={archiveMutation}
        showArchived={archived}
        onToggleArchived={handleToggleArchived}
      />
    </ScreenWrapper>
  )
}
